import { useEffect, useMemo, useRef, useState } from "react";

export interface GroupOption { value: string; n: number }

interface Props {
  /** The program's word for its grouping dimension: "Lake", "Stream", "Waterbody". */
  noun: string;
  /** Plural of `noun`. Required rather than derived: naive +"s" turns
   *  "Waterbody" into "waterbodys". */
  nounPlural: string;
  options: GroupOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  /** Permanent link for the current group, shown so it can be copied without
   *  opening the station detail panel. */
  link: string | null;
}

/**
 * Searchable grouping filter. A plain <select> was the first instinct, but
 * these programs run to 58 waterbodies and a native select gives no counts, no
 * search and no room for the permalink affordance the council actually asked
 * for — a link they can hand to one lake association.
 */
export default function GroupFilter({ noun, nounPlural, options, value, onChange, link }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [copied, setCopied] = useState(false);
  const plural = nounPlural.toLowerCase();
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  // Close on outside click or Escape, which a custom popover has to do itself.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => { if (open) input.current?.focus(); }, [open]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return options
      .filter((o) => !needle || o.value.toLowerCase().includes(needle))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [options, q]);

  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard unavailable; the link is still shareable from the URL bar */ }
  }

  return (
    <div className="groupfilter" ref={box}>
      <div className="gf-row">
        <button
          className={"gf-trigger" + (value ? " on" : "")}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span className="gf-noun">{noun}</span>
          <span className="gf-value">{value ?? `All ${plural}`}</span>
          <svg viewBox="0 0 10 6" width="10" height="6" aria-hidden="true" className="gf-caret">
            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" />
          </svg>
        </button>

        {value && (
          <>
            <button className="gf-clear" onClick={() => onChange(null)} title={`Show all ${plural}`}>
              <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" fill="none" />
              </svg>
              <span className="sr-only">Clear {noun.toLowerCase()} filter</span>
            </button>
            <button className="gf-link" onClick={copy} title={link ?? ""}>
              {copied ? "Link copied" : "Copy link"}
            </button>
          </>
        )}
      </div>

      {open && (
        <div className="gf-pop" role="listbox" aria-label={`Select a ${noun.toLowerCase()}`}>
          <input
            ref={input}
            className="gf-search"
            type="search"
            placeholder={`Search ${options.length} ${plural}`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <ul className="gf-list">
            <li>
              <button
                className={"gf-opt" + (value === null ? " on" : "")}
                onClick={() => { onChange(null); setOpen(false); setQ(""); }}
                role="option"
                aria-selected={value === null}
              >
                All {plural}
                <b>{options.reduce((a, o) => a + o.n, 0)}</b>
              </button>
            </li>
            {shown.map((o) => (
              <li key={o.value}>
                <button
                  className={"gf-opt" + (value === o.value ? " on" : "")}
                  onClick={() => { onChange(o.value); setOpen(false); setQ(""); }}
                  role="option"
                  aria-selected={value === o.value}
                >
                  {o.value}
                  <b>{o.n}</b>
                </button>
              </li>
            ))}
            {shown.length === 0 && <li className="gf-empty">No {noun.toLowerCase()} matches that.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
